import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useUserStore } from '~/stores/user'

import { provideFetchChartTags } from '~/api-core/Charts/FetchChartTags'

import type { ChartsData } from '~/types/ChartsPage'
import type { LocationQueryValue } from 'vue-router'

/**
 * Composable for scrolling the track into view.
 *
 * When the route has a "trackId" query param, we need to scroll to the correct track on the page
 * this can be tricky since the track list is virtualized with Recycle Scroller
 * Two things to note:
 *   1. We're using useAsyncData to get all tracks needed so that the data is preloaded before we scroll
 *   2. Since Recycle Scroller only renders a certain number of items, we may need to scroll down to load more items
 *      before we can scroll to the correct track
 */
export function useSrollTrackIntoView(
  trackIdQueryParam: LocationQueryValue | LocationQueryValue[],
  chartsData: globalThis.Ref<ChartsData>,
  handleTrackSelected: (payload: { trackId: number }) => unknown,
  handleMobileChartsPlayerClose: () => void,
) {
  const { IS_LOGGED_IN } = storeToRefs(useUserStore())

  onMounted(() => {
    const trackId = Number(trackIdQueryParam) || 0

    // only scroll if the trackId is a number and the track has been found in the data
    if (trackId && chartsData.value?.foundTrack) {
      handleTrackSelected({ trackId })
      handleMobileChartsPlayerClose()

      // using raf ensures that Recycle Scroller has rendered the list and applied its styling before we check for the element
      requestAnimationFrame(() => {
        const el = document.getElementById(`track-${trackId}`)
        recursivelyScrollElementIntoView(el)
      })
    }
  })

  function recursivelyScrollElementIntoView(el: HTMLElement | null) {
    const { top } = el?.getBoundingClientRect() || { top: 0 }
    const elDistanceFromTop = window.pageYOffset + top

    // the element is visible in the viewport so we can scroll directly to it while offsetting the navbar height
    if (el && elDistanceFromTop >= 0) {
      requestAnimationFrame(() => {
        const DEFAULT_NAV_BAR_HEIGHT = IS_LOGGED_IN.value ? 0 : 76
        const ADDITIONAL_PADDING_OFFSET = 8
        const { height: navbarHeight } = (
          document.querySelector('header') as HTMLElement | null
        )?.getBoundingClientRect() || { height: DEFAULT_NAV_BAR_HEIGHT }
        const offsetPosition =
          elDistanceFromTop - navbarHeight - ADDITIONAL_PADDING_OFFSET
        const scrollingElement =
          document.scrollingElement || document.documentElement

        scrollingElement.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      })

      return
    }

    /*
     * If the element isn't in the viewport, scroll down to where it should be be based on its array index & row height.
     * The Recycle Scroller will load more items so we can pinpoint the element afterwards.
     */
    const MIN_POSSIBLE_ITEMS = 20
    const itemRowHeight = el?.offsetHeight || 78 // 78 is min height of a track item
    const recycleScrollerEl = document.getElementById(
      'charts-track-list',
    ) as HTMLElement | null
    const trackIndexPosition =
      chartsData.value?.results.findIndex(
        (track) => track.track_id === chartsData.value?.foundTrack?.track_id,
      ) || 0
    const listPosFromTop =
      recycleScrollerEl?.getBoundingClientRect().top ?? 0 + window.scrollY
    const scrollHeight =
      (trackIndexPosition > 0 ? trackIndexPosition : MIN_POSSIBLE_ITEMS) *
        itemRowHeight +
      listPosFromTop

    // Using a timeout gives time for the scrolling animation to run and for the Recycle Scroller to load more items
    return setTimeout(() => {
      const scrollingElement =
        document.scrollingElement || document.documentElement
      scrollingElement.scrollTop = scrollingElement.scrollTop + scrollHeight

      return recursivelyScrollElementIntoView(el)
    }, 650)
  }
}

export async function usePageChartsTags(shouldUseUrlTags = true) {
  const { coreFetch } = useProvideCoreFetch()
  const {
    params: { slug: lastParam },
  } = useRoute()
  const [paramCountry, paramGenre] =
    typeof lastParam === 'string' ? [] : lastParam

  const stateKeySpecifier = shouldUseUrlTags
    ? 'USING_PAGE_PARAM'
    : 'NO_PAGE_PARAM'
  const selectedCountryName = useState<string | 'all'>(
    `CHARTS_PAGE_SELECTED_COUNTRY_${stateKeySpecifier}`,
    () => paramCountry || 'all',
  )
  const selectedGenreName = useState<string | 'all'>(
    `CHARTS_PAGE_SELECTED_TAG_${stateKeySpecifier}`,
    () => paramGenre || 'all',
  )
  selectedCountryName.value = shouldUseUrlTags ? paramCountry || 'all' : 'all'
  selectedGenreName.value = shouldUseUrlTags ? paramGenre || 'all' : 'all'

  const { data } = await useAsyncData(
    `GET_CHART_TAGS_${paramCountry === 'all' ? undefined : paramCountry || 'no-filter'}`,
    async () => {
      const getChartTags = provideFetchChartTags(coreFetch)
      return getChartTags(paramCountry === 'all' ? undefined : paramCountry)
    },
    {
      default: () => ({
        countries: [] as string[],
        genres: [] as string[],
      }),
      dedupe: 'defer',
      // prevent spamming this call since this gets called every time a genre/country is selected
      // and we don't want to refetch when only the genre changes or if we already have the tags data for a given country
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
    },
  )

  if (
    selectedGenreName.value !== 'all' &&
    !data.value.genres.includes(selectedGenreName.value)
  )
    throw createError({ statusCode: 404 })

  if (
    selectedCountryName.value !== 'all' &&
    !data.value.countries.includes(selectedCountryName.value)
  )
    throw createError({ statusCode: 404 })

  const tagsDataForFetch = computed(() => ({
    country:
      selectedCountryName.value === 'all'
        ? undefined
        : selectedCountryName.value,
    genre:
      selectedGenreName.value === 'all' ? undefined : selectedGenreName.value,
  }))

  const sortedAvailableCountries = computed(() => {
    // Sort the countries alphabetically
    const sorted = data.value.countries.toSorted((a, b) => a.localeCompare(b))

    // Place "US" 1st in the list
    const usTagName = 'us'
    const usIndex = sorted.indexOf(usTagName)
    if (usIndex > -1) {
      sorted.splice(usIndex, 1)
      sorted.unshift(usTagName)
    }

    // Place "france" 2nd in the list (or 1st if "US" isn't in the list)
    const franceTagName = 'france'
    const franceIndex = sorted.indexOf(franceTagName)
    if (franceIndex > -1) {
      sorted.splice(franceIndex, 1)
      const usIndex = sorted.indexOf(usTagName)
      sorted.splice(usIndex + 1, 0, franceTagName)
    }

    return sorted
  })

  const sortedAvailableGenres = computed(() => {
    // Sort the genres alphabetically
    return data.value.genres.toSorted((a, b) => a.localeCompare(b))
  })

  return {
    selectedCountryName,
    selectedGenreName,
    sortedAvailableCountries,
    sortedAvailableGenres,
    availableCountries: data.value.countries,
    availableGenres: data.value.genres,
    tagsDataForFetch,
  } as const
}
